import { Login } from "@kalyzee/kast-web-sdk/adapted-web-components";
import { AuthenticationMethod, AuthenticationResult } from "@kalyzee/kast-web-sdk/web-components";

import { useEffect, useMemo, useState } from "react";
import { toastError } from "../../../helpers/toast";
import "./login.css";
import styles from "./login.module.css";

import { ReactIcons } from "@kalyzee/kalyzee-ui";
import { useAuthContext } from "@kalyzee/kast-web-sdk";
import { AUTH_TYPE } from "@kalyzee/kast-web-sdk/http";
import { MasterUI } from "../../../components/common/MasterUI";
import { MainClockRenderer } from "../../../components/renderers/mainClock";
import { AnimatedBackground } from "../../../components/utils/AnimatedBackground";
import { AppLoadingView } from "../../../components/utils/AppLoadingView";
import { useAppStoreSelector } from "../../../contexts/appStore/selectors";
import { useSdkContext } from "../../../contexts/sdk/context";

const AUTH_TYPE_TO_METHOD: Partial<Record<AUTH_TYPE, AuthenticationMethod>> = {
  [AUTH_TYPE.LOCAL]: AuthenticationMethod.Local,
  [AUTH_TYPE.GOOGLE]: AuthenticationMethod.Google,
  [AUTH_TYPE.AZURE_AD]: AuthenticationMethod.Microsoft,
};

const LoginPage = () => {
  const { setTokens } = useAuthContext();

  const [oAuthError, setOAuthError] = useState<boolean>();

  const sdkCtx = useSdkContext();
  const authMethods = useAppStoreSelector((state) => state.authMethods);

  const enabledMethods = useMemo(() => {
    if (!authMethods) return undefined;
    const result: AuthenticationMethod[] = [];
    for (const type of authMethods) {
      const mapped = AUTH_TYPE_TO_METHOD[type];
      if (mapped && !result.includes(mapped)) result.push(mapped);
    }
    return result;
  }, [authMethods]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('backend_error');
    if (error) {
      setOAuthError(true);
      console.error('Error while authenticating with Google: ', error);
    }
  }, []);

  const onResult = async (result: AuthenticationResult) => {
    if (result.success) {
      const tokens = result.tokens;
      if (tokens?.token) {
        setTokens({ token: tokens.token, refreshToken: tokens.refreshToken });
      }
    } else {
      toastError("Identifiants incorrectes")
    }
  };

  const renderAuth = () => {
    return (
      <Login
        className={styles.login}
        sdk={sdkCtx.client}
        logo={null}
        methods={enabledMethods ?? []}
        oAuthRedirectUri={window.origin}
        onResult={onResult}
        googleAuthentificationProps={oAuthError ? {
          error: 'Une erreur est survenue',
        } : undefined}
        localAuthentificationProps={{
          placeholderEmail: 'Email',
          placeholderPassword: 'Mot de passe'
        }}
      />
    );
  }

  const renderDateAndTime = () => {
    return (
      <MainClockRenderer
        render={date => {
          let stringDate = date.toLocaleDateString(undefined, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          });
          stringDate = stringDate.charAt(0).toUpperCase() + stringDate.slice(1);
          const stringTime = date.toLocaleTimeString(undefined, {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          });
          return (
            <div className={styles.dateTime}>
              <span className={styles.date}>{stringDate}</span>
              <span className={styles.time}>{stringTime}</span>
            </div>
          );
        }}
      />
    )
  };

  if (!authMethods) {
    return <AppLoadingView message="Récupération des méthodes d'authentification" />;
  }

  return (
    <MasterUI>
      <AnimatedBackground contentClassName={styles.backgroundHome}>
        <div className={styles.headerHome}>
          <ReactIcons.LogoFullAnimated
            style={{ width: 300 }}
          />
          <span className={styles.logo}>
            KALYZÉE
            <span className={styles.logoMeet}>
              {"Connect"}
            </span>
          </span>
        </div>
        <div className={styles.mainHome}>
          {renderAuth()}
        </div>
        <div className={styles.footerHome}>
          {renderDateAndTime()}
        </div>
      </AnimatedBackground>
    </MasterUI>
  );
};

export default LoginPage;
